import React, { useEffect, useImperativeHandle, useMemo } from 'react';
import { actions, defaultGroupByFn, useBlockLayout, useColumnOrder, useExpanded, useGlobalFilter, useGroupBy, usePagination, useResizeColumns, useRowSelect, useRowState, useSortBy, useTable } from 'react-table';
import { useInsertSelectRowColumn } from './SelectRowColumn.js';
import { TableContext } from './TableContext.js';
import { useInsertSortRowColumn } from './useInsertSortRowColumn.js';
const defaultProps = {
    autoResetExpanded: false,
    autoResetGlobalFilter: false,
    autoResetGroupBy: false,
    autoResetPage: true,
    autoResetSortBy: false,
    columns: [],
    data: [],
    defaultColumn: {},
    enableColumnOrder: false,
    enableColumnSort: false,
    enableResizeColumns: false,
    enableRowSelect: false,
    enableRowSort: false,
    enablePagination: false,
    globalFilter: defaultGlobalFilter,
    initialState: {},
    isLoading: false,
    isBackgroundFetching: false,
    manualGroupBy: false,
    groupByFn: defaultGroupByFn,
    manualPagination: false,
    manualSortBy: false,
    pageCount: -1,
    rowSelectMode: 'multiple',
    overrideHooks: []
};
/**
 * The main entry point for the table. Pass in the basic table props to `TableProvider` and it will provide a `tableInstance` via context to its children.
 *
 * See the examples for common use cases.
 *
 * The table is implemented with [react-table](https://react-table-omega.vercel.app/docs/overview).
 *
 * We apply the following hooks to `useTable`:
 *
 * - [useColumnOrder](https://react-table-omega.vercel.app/docs/api/useColumnOrder)
 * - [useResizeColumns](https://react-table-omega.vercel.app/docs/api/useResizeColumns)
 * - [useGroupBy](https://react-table-omega.vercel.app/docs/api/useGroupBy)
 * - [useGlobalFilter](https://react-table-omega.vercel.app/docs/api/useGlobalFilter)
 * - [useSortBy](https://react-table-omega.vercel.app/docs/api/useSortBy)
 * - [useExpanded](https://react-table-omega.vercel.app/docs/api/useExpanded)
 * - [usePagination](https://react-table-omega.vercel.app/docs/api/usePagination)
 *
 * ### Import Guide
 *
 * ```jsx
 * import { ConnectedTable, TableProvider } from "@panwds/react-table";
 * ```
 *
 */
export function TableProvider(props) {
    const { children, columns, data, defaultColumn, enableColumnOrder, enableColumnSort, enablePagination, enableResizeColumns, enableRowSelect, rowSelectMode, manualGroupBy, groupByFn, manualPagination, manualSortBy, onColumnOrderChange, onColumnVisibilityChange, onColumnWidthChange, onGroupByChange, onPaginationChange, onSelectedRowsChange, onSortChange, onRowSortEnd, onStateChange, onTableFilterChange, pageCount, tableInstanceRef, globalFilter, stateReducer, enableRowSort, onRowSelect, onAllRowSelect, overrideHooks, ...rest } = props;
    const initialState = useMemo(() => makeInitialState(props), []);
    /* istanbul ignore next */
    if (enableRowSort && (enablePagination || enableColumnSort || manualGroupBy)) {
        console.error('Row sorting is not compatible with pagination, column sorting, or grouping.');
    }
    const tableInstance = useTable({
        ...rest,
        columns: columns,
        originalColumns: columns,
        data,
        defaultColumn: { stickyPosition: 'right', ...defaultColumn },
        disableColumnOrder: !enableColumnOrder,
        disableGroupBy: enableRowSort,
        disableResizing: !enableResizeColumns,
        disableRowSelect: !enableRowSelect,
        disableSortBy: !enableColumnSort,
        disablePagination: !enablePagination,
        enableRowSort: enableRowSort,
        globalFilter,
        manualPagination: manualPagination || manualGroupBy,
        manualGroupBy: manualGroupBy || manualPagination,
        groupByFn: groupByFn,
        manualSortBy,
        initialState,
        pageCount,
        stateReducer: (newState, action, prevState) => {
            let state;
            switch (action.type) {
                case actions['toggleRowSelected']:
                    if (rowSelectMode === 'single') {
                        state = {
                            ...newState,
                            selectedRowIds: { [action['id']]: true }
                        };
                    }
                    else {
                        state = newState;
                    }
                    break;
                case 'resetColumnWidths': {
                    const { columnWidths } = action;
                    state = {
                        ...newState,
                        columnResizing: {
                            ...newState.columnResizing,
                            columnWidths: columnWidths
                        }
                    };
                    break;
                }
                default:
                    state = newState;
            }
            return stateReducer ? stateReducer(state, action, prevState) : state;
        }
    }, useBlockLayout, useColumnOrder, useResizeColumns, useGlobalFilter, useGroupBy, useSortBy, useExpanded, usePagination, useRowSelect, useInsertSelectRowColumn, useInsertSortRowColumn, useRowState, ...(overrideHooks || []));
    useImperativeHandle(tableInstanceRef, () => tableInstance);
    useEffect(() => {
        onColumnOrderChange?.(tableInstance.state.columnOrder);
    }, [onColumnOrderChange, tableInstance.state.columnOrder]);
    useEffect(() => {
        onColumnVisibilityChange?.(tableInstance.state.hiddenColumns);
    }, [onColumnVisibilityChange, tableInstance.state.hiddenColumns]);
    useEffect(() => {
        onColumnWidthChange?.(tableInstance.state.columnResizing.columnWidths);
    }, [onColumnWidthChange, tableInstance.state.columnResizing.columnWidths]);
    useEffect(() => {
        onGroupByChange?.(tableInstance.state.groupBy);
    }, [onGroupByChange, tableInstance.state.groupBy]);
    useEffect(() => {
        onPaginationChange?.({
            pageIndex: tableInstance.state.pageIndex,
            pageSize: tableInstance.state.pageSize
        });
    }, [onPaginationChange, tableInstance.state.pageIndex, tableInstance.state.pageSize]);
    useEffect(() => {
        onSelectedRowsChange?.(tableInstance.state.selectedRowIds);
    }, [onSelectedRowsChange, tableInstance.state.selectedRowIds]);
    useEffect(() => {
        onSortChange?.(tableInstance.state.sortBy);
    }, [onSortChange, tableInstance.state.sortBy]);
    useEffect(() => {
        onStateChange?.(tableInstance.state);
    }, [onStateChange, tableInstance.state]);
    useEffect(() => {
        onTableFilterChange?.(tableInstance.state.globalFilter);
    }, [onTableFilterChange, tableInstance.state.globalFilter]);
    return (React.createElement(TableContext.Provider, { value: {
            enableColumnOrder,
            enableColumnSort,
            enablePagination,
            enableResizeColumns,
            enableRowSelect,
            enableRowSort,
            onRowSortEnd,
            rowSelectMode,
            onRowSelect,
            onAllRowSelect,
            manualPagination: manualPagination || manualGroupBy,
            manualGroupBy: manualGroupBy || manualPagination,
            ...tableInstance
        } }, children));
}
TableProvider.defaultProps = defaultProps;
function makeInitialState(props) {
    const pageSize = props.initialState?.pageSize || 25;
    const columnOrder = props.initialState?.columnOrder || props.columns.map((c) => c?.id || c.accessor);
    const hiddenColumns = props.initialState?.hiddenColumns ||
        props.columns.filter((c) => c.defaultHidden).map((c) => c?.id || c.accessor);
    return { ...props.initialState, columnOrder, hiddenColumns, pageSize };
}
function defaultGlobalFilter(rows, ids, filterValue) {
    const cellsToString = ids.reduce((acc, id) => {
        const cell = rows[0]?.cells.find((cell) => cell?.column?.id === id);
        acc[id] = cell?.column.cellToString;
        return acc;
    }, {});
    return rows.filter((row) => ids.some((id) => {
        const column = cellsToString[id]?.({ row, value: row.values[id] }) ?? String(row.values[id]);
        return column.toLowerCase().includes(String(filterValue).toLowerCase());
    }));
}
//# sourceMappingURL=TableProvider.js.map